/**
 * AudioContext - Comprehensive Audio Player Context Provider - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Advanced React Context for managing audio playback across multiple platforms including
 * Spotify, SoundCloud, Anchor, PodLink, and direct audio files. Features modal positioning,
 * mini player functionality, persistent iframe management, auto-play capabilities, and
 * comprehensive audio state management with platform-specific URL conversion.
 * 
 * @fileoverview Audio context provider with multi-platform support and advanced UI controls
 * @author John Bellew - Infinia.ie
 * @version 1.0.0
 * @since 01/07/2025
 */

import React, { createContext, useContext, useState, useRef } from 'react';

const AudioContext = createContext();

/**
 * Custom hook to access audio context
 * 
 * @returns {Object} Audio context value with state and methods
 * @throws {Error} When used outside of AudioProvider
 * 
 * @example
 * const { playAudio, isPlaying, currentAudio } = useAudio();
 */
export const useAudio = () => {
    const context = useContext(AudioContext);
    if (!context) {
        throw new Error('useAudio must be used within an AudioProvider');
    }
    return context;
};

/**
 * Audio Context Provider Component
 * 
 * Provides comprehensive audio functionality including platform detection,
 * modal management, playback controls, and state management for the entire
 * application's audio system.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with audio context
 * @returns {JSX.Element} AudioContext.Provider with audio state and methods
 * 
 * @example
 * <AudioProvider>
 *   <App />
 * </AudioProvider>
 */
export const AudioProvider = ({ children }) => {
    // Audio state
    const [currentAudio, setCurrentAudio] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isEmbedded, setIsEmbedded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    // UI state
    const [showFullModal, setShowFullModal] = useState(false);
    const [showMiniPlayer, setShowMiniPlayer] = useState(false);
    
    // Audio element ref
    const audioRef = useRef(null);
    
    // Persistent iframe ref for embedded content
    const persistentIframeRef = useRef(null);

    /**
     * Determines audio platform information from URL
     * 
     * @param {string} url - Audio URL to analyze
     * @returns {Object} Platform information object
     * @returns {boolean} returns.isEmbedded - Whether the platform supports embedding
     * @returns {string} returns.platform - Platform name (spotify, soundcloud, etc.)
     * @returns {boolean} returns.isExternal - Whether platform requires external navigation
     * 
     * @example
     * const info = getAudioPlatformInfo('https://open.spotify.com/episode/123');
     * // Returns: { isEmbedded: true, platform: 'spotify' }
     */
    const getAudioPlatformInfo = (url) => {
        if (!url) return { isEmbedded: false, platform: null };
        
        if (url.includes('spotify.com')) {
            return { isEmbedded: true, platform: 'spotify' };
        } else if (url.includes('soundcloud.com')) {
            return { isEmbedded: true, platform: 'soundcloud' };
        } else if (url.includes('anchor.fm')) {
            return { isEmbedded: true, platform: 'anchor' };
        } else if (url.includes('pod.link')) {
            return { isEmbedded: true, platform: 'podlink' };
        } else if (url.includes('goloudplayer.com')) {
            return { isEmbedded: false, platform: 'goloud', isExternal: true };
        }
        
        return { isEmbedded: false, platform: 'direct' };
    };

    /**
     * Converts platform URLs to their embeddable format
     * 
     * @param {string} url - Original platform URL
     * @returns {string} Embeddable URL format for iframes
     * 
     * @example
     * const embedUrl = getEmbeddableUrl('https://open.spotify.com/episode/123');
     * // Returns: 'https://open.spotify.com/embed/episode/123?utm_source=generator&theme=0'
     */
    const getEmbeddableUrl = (url) => {
        
        if (url.includes('spotify.com/episode/')) {
            const episodeId = url.split('episode/')[1].split('?')[0];
            const embedUrl = `https://open.spotify.com/embed/episode/${episodeId}?utm_source=generator&theme=0`;
            return embedUrl;
        } else if (url.includes('spotify.com/show/')) {
            const showId = url.split('show/')[1].split('?')[0];
            const embedUrl = `https://open.spotify.com/embed/show/${showId}?utm_source=generator&theme=0`;
            return embedUrl;
        } else if (url.includes('soundcloud.com/')) {
            const embedUrl = `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`;
            return embedUrl;
        } else if (url.includes('anchor.fm/')) {
            const embedUrl = url.replace('anchor.fm/', 'anchor.fm/embed/');
            return embedUrl;
        } else if (url.includes('goloudplayer.com')) {
            // GoLoud uses direct URLs - the link.goloudplayer.com should redirect to the embeddable player
            return url;
        }
        
        return url;
    };

    /**
     * Initiates audio playback with modal positioning and platform handling
     * 
     * @param {Object} audioItem - Audio item object containing metadata
     * @param {string} audioItem.webURLLink - URL to the audio content
     * @param {string} audioItem.title - Audio title for display
     * @param {Object|null} mousePosition - Optional mouse position for modal placement
     * @param {number} mousePosition.x - X coordinate of mouse click
     * @param {number} mousePosition.y - Y coordinate of mouse click
     * 
     * @example
     * playAudio({
     *   webURLLink: 'https://example.com/audio.mp3',
     *   title: 'My Podcast Episode'
     * }, { x: 100, y: 200 });
     */
    const playAudio = (audioItem, mousePosition = null) => {
       
        const platformInfo = getAudioPlatformInfo(audioItem.webURLLink);
        
        // Handle external-only platforms (like GoLoud) that can't be embedded
        if (platformInfo.isExternal) {
            window.open(audioItem.webURLLink, '_blank');
            return;
        }
        
        // Stop any currently playing audio
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        
        setCurrentAudio(audioItem);
        setIsEmbedded(platformInfo.isEmbedded);
        setIsLoading(platformInfo.isEmbedded);
        setShowFullModal(true);
        setShowMiniPlayer(false);
        setIsPlaying(true); // Auto-play for all audio types
        setCurrentTime(0);
        setDuration(0);
        
        // Store mouse position for modal positioning (ONLY for audio modals)
        if (mousePosition) {
             // Set them with a delay to ensure modal is rendered
            setTimeout(() => {
                // Use very specific selectors to avoid conflicts with other modals
                const modalOverlay = document.querySelector('.audio-modal-overlay:not(.pdf-modal-overlay):not(.video-modal-overlay)');
                const modal = document.querySelector('.audio-modal:not(.pdf-modal):not(.video-modal)');
                
                if (modalOverlay && modal) {
                    // Add cursor positioning classes ONLY to audio modals
                    modalOverlay.classList.add('cursor-positioned');
                    modal.classList.add('cursor-positioned');
                    
                    // Only adjust modal positioning, leave overlay as-is
                    modal.style.position = 'fixed';
                    modal.style.top = '50vh';
                    modal.style.left = '50vw';
                    modal.style.transform = 'translate(-50%, -50%)';
                    modal.style.zIndex = '10001';
                }
            }, 150);
        }
        
        // Auto-play direct audio files
        if (!platformInfo.isEmbedded) {
            setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.play().then(() => {
                        setIsPlaying(true);
                    }).catch(error => {
                        console.warn('Audio autoplay failed:', error);
                        setIsPlaying(false);
                    });
                }
            }, 200); // Small delay to ensure audio element is ready
        }
    };
    
    /**
     * Updates persistent iframe position based on current UI state
     * Handles modal and mini-player positioning for embedded content
     */
    const updatePersistentIframePosition = () => {
        if (!persistentIframeRef.current) return;
        
        const iframe = persistentIframeRef.current;
        
        if (showFullModal) {
            // Position in modal content area
            iframe.style.cssText = `
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 90vw;
                max-width: 520px;
                height: 352px;
                opacity: 1;
                pointer-events: auto;
                border: none;
                z-index: 10001;
                border-radius: 12px;
                box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            `;
        } else {
            // Hide but keep playing
            iframe.style.cssText = `
                position: fixed;
                top: -1000px;
                left: -1000px;
                width: 1px;
                height: 1px;
                opacity: 0;
                pointer-events: none;
                border: none;
                z-index: -1;
                border-radius: 12px;
            `;
        }
    };

    /**
     * Closes all audio players and cleans up resources
     * Handles blob URL cleanup, modal state reset, and iframe removal
     */
    const closeAudio = () => {
        
        // Clean up blob URLs to prevent memory leaks
        if (currentAudio && currentAudio.webURLLink && currentAudio.isLocalBlob) {
            URL.revokeObjectURL(currentAudio.webURLLink);
        }
        
        setCurrentAudio(null);
        setShowFullModal(false);
        setShowMiniPlayer(false);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        
        // Stop audio if it's playing
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        
        // Remove persistent iframe
        if (persistentIframeRef.current) {
            persistentIframeRef.current.remove();
            persistentIframeRef.current = null;
        }
        
        // Clean up cursor positioning classes and styles (ONLY for audio modals)
        setTimeout(() => {
            // Use specific selectors for audio modals only
            const modalOverlay = document.querySelector('.audio-modal-overlay');
            const modal = document.querySelector('.audio-modal');
            if (modalOverlay) {
                modalOverlay.classList.remove('cursor-positioned');
            }
            if (modal) {
                modal.classList.remove('cursor-positioned');
                modal.style.removeProperty('--cursor-x');
                modal.style.removeProperty('--cursor-y');
            }
        }, 100);
    };

    /**
     * Minimizes audio player to mini player mode
     * Transitions from full modal to compact top-positioned mini player
     */
    const minimizeToMini = () => {
        setShowFullModal(false);
        setShowMiniPlayer(true);
        
        // Auto-scroll to top when mini player appears (since it's positioned at top)
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    /**
     * Restores mini player to full modal view
     * Positions modal in center of current viewport with smooth transition
     */
    const restoreToFull = () => {
        setShowFullModal(true);
        setShowMiniPlayer(false);
        
        // Position modal in center of current viewport (like when first opened)
        setTimeout(() => {
            // Use specific selectors for audio modals only
            const modalOverlay = document.querySelector('.audio-modal-overlay');
            const modal = document.querySelector('.audio-modal');
            
            if (modalOverlay && modal) {
                // Add cursor positioning classes for viewport centering ONLY to audio modals
                modalOverlay.classList.add('cursor-positioned');
                modal.classList.add('cursor-positioned');
                
                // Position modal in center of current viewport
                const viewportCenterX = window.innerWidth / 2;
                const viewportCenterY = window.innerHeight / 2 + window.scrollY;
                
                // Apply positioning ONLY to audio modals
                modalOverlay.style.alignItems = 'flex-start';
                modalOverlay.style.justifyContent = 'flex-start';
                modalOverlay.style.padding = '0';
                
                modal.style.position = 'absolute';
                modal.style.top = `${viewportCenterY}px`;
                modal.style.left = `${viewportCenterX}px`;
                modal.style.transform = 'translate(-50%, -50%)';
            }
            
            // Update iframe position after state change
            updatePersistentIframePosition();
        }, 150);
    };

    /**
     * Toggles playback state for direct audio files
     * Note: Embedded players cannot be controlled directly
     */
    const togglePlayback = () => {
        if (isEmbedded) {
            // Can't control embedded players directly
            return;
        }
        
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
                setIsPlaying(false);
            } else {
                audioRef.current.play().then(() => {
                    setIsPlaying(true);
                }).catch(error => {
                    console.warn('Audio playback failed:', error);
                });
            }
        }
    };

    /**
     * Formats time duration for display
     * 
     * @param {number} timeInSeconds - Time duration in seconds
     * @returns {string} Formatted time string (e.g., "3:45")
     * 
     * @example
     * formatTime(225) // Returns "3:45"
     * formatTime(0) // Returns "0:00"
     */
    const formatTime = (timeInSeconds) => {
        if (!timeInSeconds || isNaN(timeInSeconds)) return '0:00';
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = Math.floor(timeInSeconds % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const value = {
        // State
        currentAudio,
        isPlaying,
        currentTime,
        duration,
        volume,
        isEmbedded,
        isLoading,
        showFullModal,
        showMiniPlayer,
        audioRef,
        
        // Actions
        playAudio,
        closeAudio,
        minimizeToMini,
        restoreToFull,
        togglePlayback,
        
        // Setters
        setIsPlaying,
        setCurrentTime,
        setDuration,
        setVolume,
        setIsLoading,
        
        // Helpers
        getEmbeddableUrl,
        formatTime,
        getAudioPlatformInfo
    };

    return (
        <AudioContext.Provider value={value}>
            {/* Persistent audio element for direct audio files */}
            {currentAudio && !isEmbedded && (
                <audio
                    ref={audioRef}
                    src={currentAudio.webURLLink}
                    preload="metadata"
                    onTimeUpdate={(e) => {
                        setCurrentTime(e.target.currentTime);
                        setDuration(e.target.duration || 0);
                    }}
                    onLoadedMetadata={(e) => {
                        setDuration(e.target.duration);
                    }}
                    onEnded={() => {
                        setIsPlaying(false);
                    }}
                    onError={(e) => {
                        console.error('Audio element error:', e);
                    }}
                    onCanPlay={() => {
                        // Audio can play - ready state
                    }}
                    volume={volume}
                    style={{ display: 'none' }}
                />
            )}
            {children}
        </AudioContext.Provider>
    );
}; 